import { useMemo } from 'react';
import './EmsSunIcon.css';

const VIEW_SIZE = 56;
const CENTER = VIEW_SIZE / 2;
const SUN_R = 14;
const RAY_INNER_R = 17;
const RAY_OUTER_R = 24;

function getInsolationColors(pct) {
  const value = Number(pct);
  if (!Number.isFinite(value)) {
    return {
      color: '#ffc94a',
      glow: 'rgba(255, 201, 74, 0.35)',
    };
  }
  if (value < 40) {
    return {
      color: '#c9a227',
      glow: 'rgba(201, 162, 39, 0.28)',
    };
  }
  if (value < 70) {
    return {
      color: '#ffc94a',
      glow: 'rgba(255, 201, 74, 0.35)',
    };
  }
  return {
    color: '#ffb020',
    glow: 'rgba(255, 176, 32, 0.42)',
  };
}

function polarToCartesian(r, angleDeg) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: CENTER + r * Math.cos(angleRad),
    y: CENTER + r * Math.sin(angleRad),
  };
}

/** More clouds and larger cover as insolation drops below 100%. */
function getCloudLayers(level) {
  if (level >= 100) return [];

  const deficit = 100 - level;
  if (deficit <= 8) {
    return [{ x: 31, y: 33, scale: 0.52, opacity: 0.82 }];
  }
  if (deficit <= 25) {
    return [
      { x: 27, y: 31, scale: 0.62, opacity: 0.86 },
      { x: 37, y: 35, scale: 0.48, opacity: 0.78 },
    ];
  }
  if (deficit <= 50) {
    return [
      { x: 24, y: 30, scale: 0.72, opacity: 0.9 },
      { x: 35, y: 33, scale: 0.58, opacity: 0.84 },
      { x: 29, y: 38, scale: 0.5, opacity: 0.8 },
    ];
  }
  return [
    { x: 22, y: 28, scale: 0.82, opacity: 0.94 },
    { x: 34, y: 31, scale: 0.68, opacity: 0.9 },
    { x: 26, y: 37, scale: 0.6, opacity: 0.88 },
    { x: 38, y: 39, scale: 0.46, opacity: 0.82 },
  ];
}

function SunCloud({ x, y, scale, opacity }) {
  return (
    <g className="ems-sun-icon__cloud" transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <ellipse cx="0" cy="4" rx="10" ry="6" />
      <ellipse cx="-7" cy="6" rx="7" ry="5" />
      <ellipse cx="7" cy="6" rx="7" ry="5" />
      <ellipse cx="-2" cy="1" rx="6" ry="5" />
      <ellipse cx="5" cy="2" rx="5" ry="4" />
    </g>
  );
}

export default function EmsSunIcon({ insolationPct, active = false, className = '' }) {
  const level = Math.min(100, Math.max(0, Number(insolationPct) || 0));
  const colors = useMemo(() => getInsolationColors(insolationPct), [insolationPct]);
  const clouds = useMemo(() => getCloudLayers(level), [level]);

  const rootClassName = ['ems-sun-icon', active ? 'ems-sun-icon--active' : '', className].filter(Boolean).join(' ');

  return (
    <div
      className={rootClassName}
      style={{
        '--ems-sun-color': colors.color,
        '--ems-sun-glow': colors.glow,
      }}
      aria-hidden
    >
      <svg className="ems-sun-icon__svg" viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}>
        {Array.from({ length: 8 }, (_, index) => {
          const angle = index * 45;
          const inner = polarToCartesian(RAY_INNER_R, angle);
          const outer = polarToCartesian(RAY_OUTER_R, angle);
          return (
            <line
              key={`ray-${index}`}
              className="ems-sun-icon__ray"
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
            />
          );
        })}

        <circle className="ems-sun-icon__body" cx={CENTER} cy={CENTER} r={SUN_R} />

        {clouds.map((cloud, index) => (
          <SunCloud key={index} {...cloud} />
        ))}
      </svg>
    </div>
  );
}
